<template>
  <Flex
    direction="column"
    v-if="hasFeatureFlag('module-messages')"
  >
    <router-view />
    <div
      v-if="firstFetchFinished && !showEmptyPageContent"
      :class="$style.actions"
    >
      <Filter v-model="variables.filter" />
      <BaseSearch
        v-model="variables.search"
        debounce
      />
    </div>
    <BaseAlert
      v-if="isRouteAllowed('messages-purchase-credits') && unleash.isEnabled('BlackFridaySMSOffer')"
      :text="$t('messages.black_friday_offer.heading')"
      :primaryAction="{
        text: $t('global.actions.claim_offer'),
        routerLink: {
          name: 'messages-purchase-credits'
        }
      }"
      v-test="'black-friday-offer'"
    />
    <BaseCard v-if="messages && messages.length">
      <MessagesList
        :messages="messages"
        :filter="variables.filter"
      />
    </BaseCard>
    <LoadMore
      v-show="messages && messages.length && !allDataFetched"
      @shown="fetchMore"
    />
    <BaseSpinner
      v-if="
        !showEmptyPageContent &&
        (!messages || !messages.length) &&
        !allDataFetched
      "
    />
    <div :class="$style.flex">
      <EmptyPageContent
        v-if="showEmptyPageContent || showNoResults"
        imageName="messages"
        :text="
          showNoResults
            ? {
                description: $t('global.no_results')
              }
            : {
                title: $t('messages.empty_state.title'),
                description: $t('messages.empty_state.description')
              }
        "
        :moreLink="
          showEmptyPageContent
            ? {
                text: $t('messages.empty_state.more_text'),
                url: helpCenterArticleUrl($t('messages.empty_state.more_url'))
              }
            : null
        "
      />
    </div>
  </Flex>
</template>

<script lang="ts">
export default {
  name: 'MessageLogs',
};
</script>

<script
  setup
  lang="ts"
>
import { computed, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePagination } from '@/apollo/pagination';
import EmptyPageContent from '@/components/EmptyPageContent.vue';
import LoadMore from '@/components/LoadMore.vue';
import { helpCenterArticleUrl } from '@/helpers/help-center';
import { isRouteAllowed } from '@/router/helpers';
import { useUserStore } from '@/stores/user';
import unleash from '@/unleash';
import Filter from './Filter.vue';
import { GET_MESSAGES } from './graphql';
import MessagesList from './MessagesList.vue';

const { hasFeatureFlag } = useUserStore();
const route = useRoute();
const router = useRouter();

const {
  messages,
  allDataFetched,
  fetchMore,
  loading,
  variables,
  firstFetchFinished,
} = usePagination({
  fieldName: 'messages',
  query: GET_MESSAGES,
  variables: {
    filter: (route.query.filter as string) || '',
    search: '',
  },
});

watchEffect(() => {
  if (route.name) {
    router.replace({
      name: route.name,
      query: variables.filter ? { filter: variables.filter } : {},
    });
  }
});

const showNoResults = computed(() => !messages.value.length && !loading.value);
const showEmptyPageContent = computed(
  () => showNoResults.value && !variables.search && !variables.filter,
);
</script>

<style
  lang="scss"
  module
>
.flex {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.actions {
  display: flex;
  align-items: flex-end;
  gap: $spacing;
}
</style>
